import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SearchFilterBar from '../../../components/ui/SearchFilterBar';
import FiltersModal from '../../../components/ui/FiltersModal';
import Pagination from '../../../components/ui/Pagination';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import ErrorState from '../../../components/ui/ErrorState';
import tuneIcon from '../../../assets/tune.svg';
import arrowDropDownIcon from '../../../assets/arrow_drop_down_filter.svg';
import { publicFeedbackService, type PublicFeedbackNomination } from '../../../services';

const PAGE_SIZE_OPTIONS = [10, 20, 30, 50];

interface PublicFilters {
  department: string;
  designation: string;
  nominationCapacity: string;
  reviewStatus: string;
  okrStatus?: string;
}

const EMPTY_FILTERS: PublicFilters = {
  department: '',
  designation: '',
  nominationCapacity: '',
  reviewStatus: '',
  okrStatus: '',
};

const External360FeedbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const uuid = searchParams.get('uuid')?.trim() || '';
  const [nominations, setNominations] = useState<PublicFeedbackNomination[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<PublicFilters>(EMPTY_FILTERS);
  const [tempFilters, setTempFilters] = useState<PublicFilters>(EMPTY_FILTERS);

  useEffect(() => {
    let isActive = true;

    if (!uuid) {
      setNominations([]);
      setApiError('A reviewer UUID is required to load feedback forms.');
      setLoading(false);
      return () => {
        isActive = false;
      };
    }

    setLoading(true);
    setApiError(null);
    publicFeedbackService.getNominations(uuid)
      .then((response) => {
        if (!isActive) return;
        if (!response.status || !response.data || !Array.isArray(response.data.nominators)) {
          setNominations([]);
          setApiError(response.message || response.error || 'Unable to load feedback forms.');
          return;
        }

        setNominations(response.data.nominators
          .filter((nominator) => Number.isFinite(nominator.nomination_relationship_id))
          .map((nominator, index) => {
            const rawRelationshipType = nominator.relationship_type?.trim() || '';
            const relationshipType = rawRelationshipType.toLowerCase();
            const nominationAs = relationshipType === 'peer' ? 'Peer'
              : relationshipType === 'superior' ? 'Superior'
                : relationshipType === 'subordinate' ? 'Subordinate'
                  : relationshipType === 'additional_subordinate' ? 'Additional Subordinate'
                  : relationshipType === 'stakeholder' ? 'Stakeholder'
                  : relationshipType === 'dotted_superior' ? 'Additional Superior'
                    : rawRelationshipType
                      ? rawRelationshipType.replace(/[_-]+/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase())
                      : 'Not specified';
            const relationshipId = String(nominator.nomination_relationship_id);

            return {
              id: relationshipId || String(index),
              relationshipId,
              employeeId: nominator.employee_id || `EMP${nominator.nominator_id}`,
              employeeName: nominator.nominator_name || nominator.nominator_email || 'Unknown',
              designation: nominator.nominator_designation || 'Not specified',
              department: nominator.nominator_department || 'Unknown',
              nominationAs,
              reviewExists: Boolean(nominator.review_exists),
            };
          }));
      })
      .catch((error: unknown) => {
        if (!isActive) return;
        setNominations([]);
        setApiError(error instanceof Error ? error.message : 'Unable to load feedback forms.');
      })
      .finally(() => {
        if (isActive) setLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [uuid, reloadKey]);

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setTempFilters(EMPTY_FILTERS);
    setSearchTerm('');
    setPage(1);
  };

  useEffect(() => {
    const handleClearFilters = () => clearFilters();
    window.addEventListener('clearFilters', handleClearFilters);
    return () => window.removeEventListener('clearFilters', handleClearFilters);
  }, []);

  const filteredNominations = useMemo(() => nominations.filter((nomination) => {
    const normalizedSearch = searchTerm.toLowerCase();
    const matchesSearch = !normalizedSearch || [
      nomination.employeeName,
      nomination.employeeId,
      nomination.department,
      nomination.designation,
      nomination.nominationAs,
    ].some((value) => value.toLowerCase().includes(normalizedSearch));
    const matchesDepartment = !filters.department || nomination.department === filters.department;
    const matchesDesignation = !filters.designation || nomination.designation === filters.designation;
    const matchesCapacity = !filters.nominationCapacity || nomination.nominationAs === filters.nominationCapacity;
    const status = nomination.reviewExists ? 'Completed' : 'Pending';
    const matchesStatus = !filters.reviewStatus || status === filters.reviewStatus;
    return matchesSearch && matchesDepartment && matchesDesignation && matchesCapacity && matchesStatus;
  }), [filters, nominations, searchTerm]);

  const pageCount = Math.max(1, Math.ceil(filteredNominations.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const currentNominations = filteredNominations.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const uniqueDepartments = [...new Set(nominations.map((nomination) => nomination.department))].sort();
  const uniqueDesignations = [...new Set(nominations.map((nomination) => nomination.designation))].sort();
  const uniqueNominationCapacities = [...new Set(nominations.map((nomination) => nomination.nominationAs))].sort();
  const uniqueReviewStatuses = ['Completed', 'Pending'];
  const activeFiltersCount = Object.values(filters).filter(Boolean).length;
  const hasSearchOrFilters = Boolean(searchTerm || activeFiltersCount);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleFilterChange = (filterType: keyof PublicFilters, value: string) => {
    setTempFilters((current) => ({ ...current, [filterType]: value }));
  };

  const applyFilters = () => {
    setFilters(tempFilters);
    setPage(1);
    setShowFilters(false);
  };

  const openFilters = () => {
    setTempFilters(filters);
    setShowFilters(true);
  };

  if (loading) return <div className="flex h-full items-center justify-center"><LoadingSpinner /></div>;
  if (apiError) {
    return (
      <ErrorState
        title={uuid ? 'Unable to Load Feedback Forms' : 'Reviewer UUID Required'}
        message={apiError}
        onRetry={uuid ? () => setReloadKey((current) => current + 1) : undefined}
        showRetryButton={Boolean(uuid)}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-6">
      <SearchFilterBar
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        onClearSearch={() => handleSearchChange('')}
        onOpenFilters={openFilters}
        hasActiveFilters={activeFiltersCount > 0}
        activeFiltersCount={activeFiltersCount}
        tuneIcon={tuneIcon}
      />

      <FiltersModal
        show={showFilters}
        tempFilters={tempFilters}
        uniqueDepartments={uniqueDepartments}
        uniqueDesignations={uniqueDesignations}
        uniqueNominationCapacities={uniqueNominationCapacities}
        uniqueReviewStatuses={uniqueReviewStatuses}
        onChange={handleFilterChange}
        onApply={applyFilters}
        onRemoveAll={clearFilters}
        onClose={() => setShowFilters(false)}
        arrowDropDownIcon={arrowDropDownIcon}
        isNominationReviewPage
      />

      {currentNominations.length === 0 ? (
        <div className="py-12 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
            <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="mb-2 text-lg font-medium text-gray-900">
            {nominations.length === 0 ? 'No Nominations Found' : 'No Results Found'}
          </h3>
          <p className="mb-4 text-gray-500">
            {nominations.length === 0
              ? 'You don\'t have any pending nomination reviews at the moment.'
              : 'No nominations found.'}
          </p>
          {hasSearchOrFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center rounded-md border border-transparent bg-purple-100 px-4 py-2 text-sm font-medium text-purple-600 hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
            >
              Clear all filters
            </button>
          )}
        </div>
      ) : (
        <div className="flex h-[440px] max-h-[calc(100vh-220px)] min-h-0 flex-none flex-col overflow-hidden">
          <div className="relative min-h-0 flex-1 overflow-auto">
            <div className="min-w-[1000px]">
              <div className="sticky top-0 z-10 mb-2 grid grid-cols-12 gap-4 rounded-lg border-b border-gray-200 bg-primary-800 px-6 py-3 text-base font-medium tracking-wider text-white">
                <div className="col-span-1 min-w-0">SNO.</div><div className="col-span-2 min-w-0">Employee ID</div>
                <div className="col-span-2 min-w-0">Employee</div><div className="col-span-2 min-w-0">Relationship</div>
                <div className="col-span-2 min-w-0">Department</div><div className="col-span-2 min-w-0">Designation</div><div className="col-span-1 min-w-0">Status</div>
              </div>
              <div className="space-y-3 pb-4">
                {currentNominations.map((nomination, index) => (
                  <button
                    key={nomination.id}
                    type="button"
                    onClick={() => navigate(`/external-360-feedback/${encodeURIComponent(nomination.relationshipId)}/${nomination.reviewExists ? 'view' : 'review'}?uuid=${encodeURIComponent(uuid)}`)}
                    className="group grid w-full grid-cols-12 items-center gap-4 overflow-hidden rounded-lg border border-gray-200 px-6 py-2 text-left transition-colors hover:bg-gray-50"
                  >
                    <span className="col-span-1 min-w-0 text-base text-gray-600">{(currentPage - 1) * pageSize + index + 1}.</span>
                    <span className="col-span-2 min-w-0 break-words text-base text-gray-600">{nomination.employeeId}</span>
                    <span className="col-span-2 min-w-0 break-words text-base text-gray-600">{nomination.employeeName}</span>
                    <span className="col-span-2 min-w-0 break-words text-base text-gray-600">{nomination.nominationAs}</span>
                    <span className="col-span-2 min-w-0 break-words text-base text-gray-600">{nomination.department}</span>
                    <span className="col-span-2 min-w-0 break-words text-base text-gray-600">{nomination.designation}</span>
                    <span className="col-span-1 flex min-w-0 items-center">
                      <span className={`inline-flex max-w-full items-center whitespace-nowrap rounded-full px-1.5 py-0.5 text-xs font-medium ${nomination.reviewExists ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {nomination.reviewExists ? 'Completed' : 'Pending'}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-3">
            <Pagination
              page={currentPage}
              pageCount={pageCount}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default External360FeedbackPage;
